/**
 * Timer state helpers for the frontend scheduler and display components.
 * Mirrors engine TimerState.ts (kept local to avoid Vite CJS named-export issues).
 */

export type TimerMode = 'blind' | 'stopwatch' | 'max' | 'min' | 'deadline';

export type TimerSoundAnchor = 'elapsed' | 'remaining' | 'deadlineRemaining';

export interface TimerSoundCue {
  audioId: string;
  anchor: TimerSoundAnchor;
  atSeconds: number;
}

export interface TimerState {
  mode: TimerMode;
  startedAt: number | null;
  stoppedAt: number | null;
  minSeconds: number | null;
  maxSeconds: number | null;
  targetAt: number | null;
  soundCues: TimerSoundCue[];
  preCountdownSeconds: number | null;
  preCountdownAudioId: string | null;
  armedAt: number | null;
}

const DEFAULT_MODE: TimerMode = 'stopwatch';
const VALID_ANCHORS: TimerSoundAnchor[] = ['elapsed', 'remaining', 'deadlineRemaining'];
const VALID_MODES: TimerMode[] = ['blind', 'stopwatch', 'max', 'min', 'deadline'];

const DEFAULT_STATE: TimerState = {
  mode: DEFAULT_MODE,
  startedAt: null,
  stoppedAt: null,
  minSeconds: null,
  maxSeconds: null,
  targetAt: null,
  soundCues: [],
  preCountdownSeconds: null,
  preCountdownAudioId: null,
  armedAt: null,
};

function isValidMode(mode: unknown): mode is TimerMode {
  return typeof mode === 'string' && VALID_MODES.includes(mode as TimerMode);
}

function isValidAnchor(anchor: unknown): anchor is TimerSoundAnchor {
  return typeof anchor === 'string' && VALID_ANCHORS.includes(anchor as TimerSoundAnchor);
}

function normaliseSoundCue(raw: unknown): TimerSoundCue | null {
  if (!raw || typeof raw !== 'object') return null;
  const entry = raw as Record<string, unknown>;
  const audioId = typeof entry.audioId === 'string' ? entry.audioId.trim() : '';
  if (!audioId) return null;
  const atSeconds = Number(entry.atSeconds);
  if (!Number.isFinite(atSeconds) || atSeconds < 0) return null;
  const anchor = isValidAnchor(entry.anchor) ? entry.anchor : 'elapsed';
  return { audioId, anchor, atSeconds };
}

export function normaliseSoundCues(raw: unknown): TimerSoundCue[] {
  if (!Array.isArray(raw)) return [];
  const cues: TimerSoundCue[] = [];
  for (const item of raw) {
    const cue = normaliseSoundCue(item);
    if (cue) cues.push(cue);
  }
  return cues;
}

export function parseTimerState(
  value: string | number | boolean | undefined | null
): TimerState {
  if (value === undefined || value === null || value === '') {
    return { ...DEFAULT_STATE, soundCues: [] };
  }
  const raw = typeof value === 'string' ? value : String(value);
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') {
      return { ...DEFAULT_STATE, soundCues: [] };
    }
    const mode = isValidMode(parsed.mode) ? parsed.mode : DEFAULT_MODE;
    const startedAt =
      typeof parsed.startedAt === 'number' && Number.isFinite(parsed.startedAt)
        ? parsed.startedAt
        : null;
    const stoppedAt =
      typeof parsed.stoppedAt === 'number' && Number.isFinite(parsed.stoppedAt)
        ? parsed.stoppedAt
        : null;
    const minSeconds =
      typeof parsed.minSeconds === 'number' &&
      Number.isFinite(parsed.minSeconds) &&
      parsed.minSeconds >= 0
        ? parsed.minSeconds
        : null;
    const maxSeconds =
      typeof parsed.maxSeconds === 'number' &&
      Number.isFinite(parsed.maxSeconds) &&
      parsed.maxSeconds >= 0
        ? parsed.maxSeconds
        : null;
    const targetAt =
      typeof parsed.targetAt === 'number' && Number.isFinite(parsed.targetAt)
        ? parsed.targetAt
        : null;
    const soundCues = normaliseSoundCues(parsed.soundCues);
    const preCountdownSeconds =
      typeof parsed.preCountdownSeconds === 'number' &&
      Number.isFinite(parsed.preCountdownSeconds) &&
      parsed.preCountdownSeconds > 0
        ? parsed.preCountdownSeconds
        : null;
    const preCountdownAudioId =
      typeof parsed.preCountdownAudioId === 'string' && parsed.preCountdownAudioId.trim().length > 0
        ? parsed.preCountdownAudioId.trim()
        : null;
    const armedAt =
      typeof parsed.armedAt === 'number' && Number.isFinite(parsed.armedAt) ? parsed.armedAt : null;
    return {
      mode,
      startedAt,
      stoppedAt,
      minSeconds,
      maxSeconds,
      targetAt,
      soundCues,
      preCountdownSeconds,
      preCountdownAudioId,
      armedAt,
    };
  } catch {
    return { ...DEFAULT_STATE, soundCues: [] };
  }
}

export function hasPreCountdownConfigured(state: TimerState): boolean {
  return state.preCountdownSeconds !== null && state.preCountdownSeconds > 0;
}

export function isTimerInPreCountdown(state: TimerState, nowMs: number): boolean {
  if (
    !hasPreCountdownConfigured(state) ||
    state.armedAt === null ||
    state.startedAt === null ||
    state.stoppedAt !== null
  ) {
    return false;
  }
  return nowMs < state.startedAt;
}

export function getPreCountdownRemainingSeconds(state: TimerState, nowMs: number): number {
  if (!isTimerInPreCountdown(state, nowMs)) return 0;
  return Math.max(1, Math.ceil((state.startedAt! - nowMs) / 1000));
}

export function resolveCueFireAtMs(state: TimerState, cue: TimerSoundCue): number | null {
  if (cue.anchor === 'elapsed') {
    if (state.startedAt === null) return null;
    return state.startedAt + cue.atSeconds * 1000;
  }

  if (cue.anchor === 'remaining') {
    if (state.mode !== 'max' || state.maxSeconds === null || state.startedAt === null) return null;
    if (cue.atSeconds > state.maxSeconds) return null;
    return state.startedAt + (state.maxSeconds - cue.atSeconds) * 1000;
  }

  if (cue.anchor === 'deadlineRemaining') {
    if (state.mode !== 'deadline' || state.targetAt === null) return null;
    return state.targetAt - cue.atSeconds * 1000;
  }

  return null;
}
